'use client';

import { CheckCircleIcon } from '@heroicons/react/24/solid';

export default function PriceGuideSection() {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Main Heading */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            5 Ton Trane AC Price in Dallas–Fort Worth
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>
        </div>

        {/* Introduction */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            5 Ton Trane AC Units: Installation & Pricing Guide for Texas Homes
          </h3>
          <div className="space-y-4 text-lg text-gray-700">
            <p>
              A 5 ton Trane air conditioning system delivers <strong>60,000 BTUs</strong> of cooling power per hour, making it ideal for larger Texas homes between <strong>2,500–3,500+ square feet</strong>.
            </p>
            <p>
              Designed for high heat performance, Trane 5 ton systems provide consistent airflow, humidity control, and reliable cooling during long Dallas–Fort Worth summers.
            </p>
            <p>
              Professional installation is essential to ensure proper load calculation, airflow balancing, and long-term system efficiency.
            </p>
          </div>
        </div>

        {/* Key Features */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8">
            Key Features of 5 Ton Trane Systems
          </h3>

          {/* High Cooling Capacity */}
          <div className="mb-8">
            <h4 className="text-2xl font-semibold text-gray-900 mb-4">
              High Cooling Capacity
            </h4>
            <p className="text-lg text-gray-700">
              5 ton Trane AC units are engineered to handle significant heat loads in large residential properties. With 60,000 BTUs of output, these systems maintain stable temperatures even during peak summer months in North Texas.
            </p>
          </div>

          {/* Energy Efficiency */}
          <div className="mb-8">
            <h4 className="text-2xl font-semibold text-gray-900 mb-4">
              Energy Efficiency (SEER2 Ratings)
            </h4>
            <p className="text-lg text-gray-700 mb-4">
              Modern 5 ton Trane systems are available in:
            </p>
            <ul className="space-y-2 text-lg text-gray-700 ml-6">
              <li>• <strong>XR14</strong> – 14.3 SEER2</li>
              <li>• <strong>XR15</strong> – 15.2 SEER2</li>
              <li>• <strong>XR16</strong> – 16.2+ SEER2</li>
            </ul>
            <p className="text-lg text-gray-700 mt-4">
              Higher SEER2 ratings reduce monthly operating costs while maintaining strong cooling performance.
            </p>
          </div>

          {/* Professional Installation Requirements */}
          <div className="mb-8">
            <h4 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Installation Requirements
            </h4>
            <p className="text-lg text-gray-700 mb-4">
              Proper installation includes:
            </p>
            <ul className="space-y-2 text-lg text-gray-700 ml-6">
              <li>• Manual J load calculation</li>
              <li>• Correct duct sizing verification</li>
              <li>• Electrical disconnect and whip upgrades</li>
              <li>• Refrigerant line inspection</li>
              <li>• Drain pan and safety switch installation</li>
              <li>• Full startup and system testing</li>
            </ul>
            <p className="text-lg text-gray-700 mt-4">
              Incorrect sizing or airflow setup can cause short cycling, humidity issues, and premature system wear.
            </p>
          </div>
        </div>

        {/* Why Homeowners Choose */}
        <div className="mb-16 bg-blue-50 rounded-2xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8">
            Why Homeowners Choose 5 Ton Trane Systems
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <span className="text-lg text-gray-700">Ideal for Large Homes in Texas</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <span className="text-lg text-gray-700">Durable Climatuff® compressor</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <span className="text-lg text-gray-700">10-Year Registered Parts Warranty</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <span className="text-lg text-gray-700">Designed for high-heat climates</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <span className="text-lg text-gray-700">Long-term energy efficiency</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <span className="text-lg text-gray-700">15–20 year lifespan when properly maintained</span>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            How Much Does a 5 Ton Trane AC Cost in Dallas?
          </h3>
          <p className="text-lg text-gray-700 mb-6">
            Pricing varies depending on:
          </p>
          <ul className="space-y-3 text-lg text-gray-700 ml-6 mb-6">
            <li>• Selected SEER2 model</li>
            <li>• Installation complexity</li>
            <li>• Ductwork condition</li>
            <li>• Electrical upgrades</li>
            <li>• Permit requirements</li>
          </ul>
          <p className="text-lg text-gray-700 mb-6">
            For homeowners in the Dallas–Fort Worth area, professional evaluation ensures accurate system sizing and transparent pricing.
          </p>
          <p className="text-lg text-gray-700 mb-8">
            Trane5Ton.com recommends <strong>HVAC Services Pro</strong> as a trusted installation partner for 5 ton Trane systems in North Texas.
          </p>
          <div className="text-center">
            <a
              href="https://www.hvacservicespro.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
            >
              👉 Schedule Your Free Installation Consultation
            </a>
          </div>
        </div>

        {/* Maintenance & Service */}
        <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            Maintenance & Service
          </h3>
          <p className="text-lg text-gray-700 mb-6">
            Routine maintenance helps extend system lifespan and maintain peak efficiency. A professional service plan typically includes:
          </p>
          <ul className="space-y-3 text-lg text-gray-700 ml-6 mb-6">
            <li>• Coil cleaning</li>
            <li>• Filter replacement</li>
            <li>• Refrigerant level inspection</li>
            <li>• Electrical component testing</li>
            <li>• Airflow performance checks</li>
          </ul>
          <p className="text-lg text-gray-700">
            Regular servicing reduces energy costs and prevents unexpected breakdowns during peak cooling season.
          </p>
        </div>
      </div>
    </section>
  );
}